import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDemo } from '../contexts/DemoContext';
import { buildApiUrl } from '../config/api';

export const useExpenses = () => {
  const { getAuthHeaders, user } = useAuth();
  const { isDemoModeActive, getDemoQueryParams } = useDemo();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all expenses
  const fetchExpenses = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const queryParams = getDemoQueryParams();
      const response = await fetch(buildApiUrl(`/api/expense${queryParams}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setExpenses(result.data || []);
      } else {
        setError(result.error || 'Failed to fetch expenses');
      }
    } catch (err) {
      console.error('Error fetching expenses:', err);
      setError('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders, getDemoQueryParams]);

  // Create new expense
  const createExpense = useCallback(async (expenseData) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      const response = await fetch(buildApiUrl('/api/expense'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(expenseData),
      });

      const result = await response.json();

      if (result.success) {
        await fetchExpenses(); // Refresh the list
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to create expense');
      }
    } catch (err) {
      console.error('Error creating expense:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchExpenses, isDemoModeActive]);

  // Update existing expense
  const updateExpense = useCallback(async (id, expenseData) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      console.log('useExpenses: Updating expense with ID:', id);
      console.log('useExpenses: Update data:', expenseData);
      
      const response = await fetch(buildApiUrl(`/api/expense/${id}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(expenseData),
      });

      const result = await response.json();

      if (result.success) {
        await fetchExpenses(); // Refresh the list
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to update expense');
      }
    } catch (err) {
      console.error('Error updating expense:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchExpenses, isDemoModeActive]);

  // Delete expense
  const deleteExpense = useCallback(async (id) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      console.log('useExpenses: Deleting expense with ID:', id);
      
      const response = await fetch(buildApiUrl(`/api/expense/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        await fetchExpenses(); // Refresh the list
        return true;
      } else {
        throw new Error(result.error || 'Failed to delete expense');
      }
    } catch (err) {
      console.error('Error deleting expense:', err);
      throw err;
    }
    }, [user, getAuthHeaders, fetchExpenses, isDemoModeActive]);

  // Get expenses by category
  const getExpensesByCategory = useCallback(async (category) => {
    if (!user) return [];

    try {
      const queryParams = getDemoQueryParams();
      const response = await fetch(buildApiUrl(`/api/expense/category/${encodeURIComponent(category)}${queryParams}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        return result.data || [];
      } else {
        throw new Error(result.error || 'Failed to fetch expenses by category');
      }
    } catch (err) {
      console.error('Error fetching expenses by category:', err);
      throw err;
    }
  }, [user, getAuthHeaders, getDemoQueryParams]);

  // Calculate summary statistics
  const calculateSummary = useCallback(() => {
    const totalAmount = expenses.reduce((sum, expense) => {
      const annualAmount = expense.amount * (expense.frequency === 'monthly' ? 12 : expense.frequency === 'quarterly' ? 4 : 1);
      return sum + annualAmount;
    }, 0);
    
    const essentialExpenses = expenses.filter(expense => expense.isEssential);
    const essentialAmount = essentialExpenses.reduce((sum, expense) => {
      const annualAmount = expense.amount * (expense.frequency === 'monthly' ? 12 : expense.frequency === 'quarterly' ? 4 : 1);
      return sum + annualAmount;
    }, 0);

    const expensesByCategory = expenses.reduce((acc, expense) => {
      const category = expense.category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(expense);
      return acc;
    }, {});

    return {
      totalExpenses: expenses.length,
      totalAmount,
      essentialAmount,
      discretionaryAmount: totalAmount - essentialAmount,
      expensesByCategory,
      categories: Object.keys(expensesByCategory)
    };
  }, [expenses]);

  // Load expenses on mount and when user or demo mode changes
  useEffect(() => {
    if (user) {
      fetchExpenses();
    } else {
      setExpenses([]);
      setLoading(false);
    }
  }, [user, fetchExpenses, isDemoModeActive]);

  return {
    expenses,
    loading,
    error,
    createExpense,
    updateExpense,
    deleteExpense,
    getExpensesByCategory,
    calculateSummary,
    refreshExpenses: fetchExpenses,
  };
};
